import { useCallback, useEffect, useState } from 'react';
import classes from 'classnames';
import classNames from './MTextarea.module.scss';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/store';
import { gameSelector } from '@/store/reducers/game';
import { authSelector } from '@/store/reducers/auth';

type MTextareaProps = {
  value: string;
  isLoading: boolean;
  onChangeValue: (text: string) => void;
  placeholder?: string | undefined;
};

const MTextarea = ({
  value = '',
  isLoading = false,
  onChangeValue,
  placeholder,
}: MTextareaProps) => {
  const { t } = useTranslation();
  const [wordCount, setContent] = useState(0);
  const { currentQuestionIndex, gameQuestions } = useAppSelector(gameSelector);
  const { interfaceLanguage } = useAppSelector(authSelector);
  const isRtlLanguage = ['he', 'ar'].includes(interfaceLanguage || '') ? true : false;
  const isResponseInEnglish =
    gameQuestions[currentQuestionIndex]?.newData?.data?.metadata?.isResponseInEnglish;

  const setFormattedContent = useCallback(
    (text: string) => {
      const words = text
        //Replace all kind of line breaks with normal "space"
        .replace(/(\r\n|\n|\r)/gm, ' ')
        .split(' ')
        .filter(Boolean);
      setContent(words.length);
      onChangeValue(text);
    },
    [setContent]
  );

  useEffect(() => {
    if (value === '') {
      setContent(0);
    }
  }, [value]);

  return (
    <div
      className={classes(
        classNames.MTextArea,
        classNames[
          isRtlLanguage ? (isResponseInEnglish ? 'textarea-ltr' : 'textarea-rtl') : 'textarea-ltr'
        ]
      )}
    >
      {isLoading ? (
        <div className={classNames.isLoading}>
          <AppLoaderCircle />
          <p>{t('weAreAnalyzing')}</p>
        </div>
      ) : (
        <>
          <textarea
            onChange={event => setFormattedContent(event.target.value)}
            value={value}
            placeholder={placeholder}
          />
          <p>{t('wordCount', { wordCount })}</p>
        </>
      )}
    </div>
  );
};

export default MTextarea;
